import {
  authenticationContext,
  cmxConfiguaration,
  cmxURL,
  windowTeardownHandler
} from '@codametrix/ui-common';

// 30 minutes — long enough to survive SSO/MFA flows.
// Checked on read via timestamp rather than a setTimeout so the TTL
// is not defeated by a tab that stays open through the whole auth flow.
const JUMP_TTL_MS = 30 * 60 * 1000;

const config = cmxConfiguaration.default;

const isExempted = (location: string) => {
  const partials = config.authenticationContext.partialUrls.some(
    exempted => location.includes(exempted)
  );
  if (partials) {
    return true;
  }
  return config.authenticationContext.exactUrls.some(
    // eslint-disable-next-line eqeqeq
    exempted => location == exempted
  );
};

const cleanUpStorage = () => {
  globalThis.sessionStorage.removeItem('jumpURL');
  globalThis.sessionStorage.removeItem('contextId');
  globalThis.sessionStorage.removeItem('jumpSavedAt');
  globalThis.sessionStorage.removeItem('jumpTenantId');
  globalThis.sessionStorage.removeItem('jumpServiceLineId');
  globalThis.sessionStorage.removeItem('deepLinkBoot');
  globalThis.sessionStorage.removeItem('deepLinkLoginMessage');
};

export const saveJump = () => {
  const currentURL = new URL(`${globalThis?.top?.location}`);
  const exempt = isExempted(currentURL.pathname);

  if (!exempt) {
    globalThis.sessionStorage.setItem('jumpURL', cmxURL.pathBuilder(currentURL));
    globalThis.sessionStorage.setItem('jumpSavedAt', `${Date.now()}`);

    const tenantId = currentURL.searchParams.get('tenantId');
    const serviceLineId = currentURL.searchParams.get('serviceLineId');
    if (tenantId) {
      globalThis.sessionStorage.setItem('jumpTenantId', tenantId);
    }
    if (serviceLineId) {
      globalThis.sessionStorage.setItem('jumpServiceLineId', serviceLineId);
    }

    if (currentURL.pathname.startsWith('/cmx/quality-assessment/workItem/')) {
      globalThis.sessionStorage.setItem('deepLinkBoot', '1');
    }

    const authorization = authenticationContext.getAuthorization();
    if (authorization.contextId !== null) {
      globalThis.sessionStorage.setItem('contextId', `${authorization.contextId}`);
    }
  }
};

const readJumpData = () => {
  const jumpURL = globalThis.sessionStorage.getItem('jumpURL') || false;
  const jumpContext = globalThis.sessionStorage.getItem('contextId');
  const savedAt = globalThis.sessionStorage.getItem('jumpSavedAt');
  const hasContext = jumpContext !== null;
  const isExpired =
    savedAt === null || Date.now() - Number.parseInt(savedAt, 10) > JUMP_TTL_MS;

  if (isExpired) {
    cleanUpStorage();
    return { url: false as false, hasContext: false, contextId: 0, tenantId: null, serviceLineId: null };
  }

  const tenantId = globalThis.sessionStorage.getItem('jumpTenantId');
  const rawServiceLineId = globalThis.sessionStorage.getItem('jumpServiceLineId');
  const serviceLineId = rawServiceLineId !== null ? Number.parseInt(rawServiceLineId, 10) : null;

  return {
    url: jumpURL,
    hasContext,
    contextId: Number.parseInt(jumpContext ?? '0', 10),
    tenantId,
    serviceLineId
  };
};

export const getJump = () => {
  const data = readJumpData();
  cleanUpStorage();
  return data;
};

export const peekJump = () => {
  return readJumpData();
};

// this is really a private function and should not be exposed.
// it is only being exposed here for testing purposes.
export const _handleUnload = (event: Event) => {
  // auth token needs to be present for page refresh.
  const authorization = authenticationContext.getAuthorization();
  if (authorization.token) {
    globalThis.sessionStorage.setItem('authToken', `${authorization.token}`);
  }
  saveJump();
};

// Use pagehide (via the teardown helper) instead of the deprecated `unload` event,
// which modern browsers block under Permissions-Policy and never fire.
windowTeardownHandler.addEventListener(_handleUnload);
